/**
 * Antriebsstrang-Simulation mit Sensor-Fusion.
 *
 * Geschwindigkeit ist die zentrale Größe und ergibt sich je nach Modus:
 *   - manuell (Tacho gehalten): klassische Gas→Beschleunigung→Speed-Simulation
 *   - Sensor (Normalfall):      Längsbeschleunigung integriert (schnelle Reaktion)
 *                               + GPS-Geschwindigkeit als driftfreie Korrektur
 *   - ohne Sensor:              sanftes Ausrollen
 *
 * Aus der Geschwindigkeit folgt die Drehzahl über den aktuellen Gang – dadurch
 * ist der Klang sauber drehzahl-/geschwindigkeitsabhängig und springt nicht.
 * Wird pro Frame mit dt (Sekunden) aufgerufen.
 */
import { state, redlineRpm, idleRpm } from '../state/store.js'
import { clamp, smooth } from '../util.js'
import { eventBoom } from '../audio/events.js'
import { getSoundModel } from '../audio/sound-models.js'

// Übersetzungen: Index = Gang (0 unbenutzt).
const RATIOS = [0, 3.72, 2.34, 1.7, 1.31, 1.04, 0.85, 0.72, 0.61]
// Speed[km/h] · Ratio · GEAR_K = Drehzahlanteil über Leerlauf.
const GEAR_K = 42

function activeSoundModel() {
  try {
    return getSoundModel(state.profile?.soundModelId)
  } catch (e) {
    return null
  }
}

function isTurbocharged(induction) {
  return (induction?.turboCount || 0) > 0 || induction?.type?.includes('turbo')
}

function activeDriveModel(model) {
  const dynamics = model?.dynamics || {}
  const rawRatios = dynamics.gearRatios?.length ? dynamics.gearRatios : RATIOS.slice(1)
  const gearRatios = rawRatios[0] === 0 ? rawRatios : [0, ...rawRatios]

  return {
    gearRatios,
    inertia: clamp(dynamics.inertia ?? 0.58, 0.2, 1),
    loadResponse: clamp(dynamics.loadResponse ?? 0.74, 0.35, 1),
    torqueCurve: dynamics.torqueCurve || [],
  }
}

function torqueAtRpm(curve, rpm) {
  if (!curve.length) return 0.72
  if (rpm <= curve[0].rpm) return curve[0].torque
  for (let i = 1; i < curve.length; i++) {
    const prev = curve[i - 1]
    const next = curve[i]
    if (rpm <= next.rpm) {
      const f = clamp((rpm - prev.rpm) / Math.max(1, next.rpm - prev.rpm), 0, 1)
      return prev.torque + (next.torque - prev.torque) * f
    }
  }
  return curve[curve.length - 1].torque
}

export function stepPhysics(dt) {
  try {
    if (!state.engine) return
    if (state.soundPreviewActive) return
    const IDLE = idleRpm()
    const red = redlineRpm()
    const soundModel = activeSoundModel()
    const induction = soundModel?.forcedInduction ?? { type: 'none', turboCount: 0 }
    const drive = activeDriveModel(soundModel)
    const ratios = drive.gearRatios
    const maxGear = Math.max(1, ratios.length - 1)
    if (!state.gear || state.gear < 1) state.gear = 1
    if (state.gear > maxGear) state.gear = maxGear

    const prevRpm = state.rpm || IDLE
    const prevSpeed = state.speed || 0
    const torque = torqueAtRpm(drive.torqueCurve, prevRpm)
    const response = 0.58 + drive.loadResponse * 0.58
    const inertiaFactor = 1.18 - drive.inertia * 0.42
    let braking = false

    // ---- Geschwindigkeit + Last je nach Modus ----
    if (state.manualHold) {
      // Manuell: Tacho gehalten = Vollgas-Simulation.
      state.throttle = smooth(state.throttle, 1, 1 - Math.pow(0.0007, dt * response))
      const drag = 0.0105 * state.speed + 0.00082 * state.speed * state.speed
      const boostPush = isTurbocharged(induction)
        ? clamp((prevRpm - (induction.spoolRpm || 2200)) / 2800, 0, 1)
        : 0
      const inductionPush =
        induction.type === 'supercharger' ? 0.18 : boostPush * (0.12 + (induction.turboCount || 0) * 0.035)
      const accel =
        state.throttle * (4.8 + torque * 4.4) * response * inertiaFactor * (1 + inductionPush) + 0.12 - drag
      state.speed = clamp(state.speed + accel * dt, 0, 280)
    } else if (state.sensor) {
      // Sensor-Fusion: echte Längsbeschleunigung integrieren …
      let aMs = state.longAccelMs || 0
      if (Math.abs(aMs) < 0.25) aMs = 0 // Totband gegen Sensorzittern
      aMs = clamp(aMs, -7, 7)
      state.speed += aMs * 3.6 * dt // m/s² → km/h
      // … und kontinuierlich gegen die GPS-Geschwindigkeit korrigieren (driftfrei).
      if (state.gpsActive) {
        const gpsAge = clamp(state.gpsAgeSec || 0, 0, 2.4)
        const accelConfidence = clamp(
          Math.max(Math.abs(aMs) / 4.2, state.sensorAccel || 0, state.sensorConfidence || 0),
          0,
          1,
        )
        const gpsLeadSec = gpsAge * clamp(0.35 + accelConfidence * 0.75, 0, 1)
        const gpsLeadKmh = clamp(aMs * 3.6 * gpsLeadSec, -18, 18)
        const gpsTarget = clamp(state.gpsSpeed + gpsLeadKmh, 0, 280)
        state.gpsLeadKmh = gpsLeadKmh
        state.speed = smooth(state.speed, gpsTarget, 1 - Math.pow(0.25, dt))
        state.gpsAgeSec = gpsAge + dt
      } else {
        // Ohne GPS: leichtes Ausrollen, damit reine Integration nicht wegdriftet.
        state.gpsLeadKmh = 0
        state.speed -= (0.4 + 0.015 * state.speed) * dt
      }
      state.speed = clamp(state.speed, 0, 280)
      // Last/Gas-Charakter aus der Beschleunigung (nicht aus rohem Sensorrauschen).
      const compensatedSpeed = state.gpsActive ? state.gpsSpeed + (state.gpsLeadKmh || 0) : state.speed
      const speedDemand = state.gpsActive ? clamp((compensatedSpeed - prevSpeed) / 36, 0, 1) : 0
      const accelDemand = Math.max(clamp(aMs / 4.2, 0, 1), state.sensorAccel || 0)
      const punchDemand = Math.max(state.sensorJerk || 0, state.gPunch || 0)
      const load = clamp(
        0.08 + accelDemand * 0.46 + speedDemand * 0.32 + punchDemand * 0.36 + Math.max(0, aMs) * 0.08,
        0,
        1,
      )
      state.throttle = smooth(state.throttle, load, 1 - Math.pow(0.018, dt * response))
      braking = aMs < -0.6
    } else {
      // Ohne Sensor: ausrollen.
      state.throttle = smooth(state.throttle, 0, 1 - Math.pow(0.02, dt))
      state.speed = clamp(state.speed - (0.6 + 0.02 * state.speed) * dt, 0, 280)
      braking = state.throttle < 0.04
    }

    // ---- Drehzahl aus Geschwindigkeit + Gang ----
    let calc = IDLE + state.speed * ratios[state.gear] * GEAR_K
    if (state.throttle > 0.18) calc += state.throttle * (120 + torque * 420) * response * inertiaFactor
    if (braking) calc -= 160
    state.rpm = smooth(state.rpm, calc, 1 - Math.pow(0.0009, dt * response * (1.2 - drive.inertia * 0.35)))
    state.rpm = clamp(state.rpm, IDLE, red * 1.03)

    if ((state.pendingShiftRpm || 0) > 0) {
      const target = clamp(state.pendingShiftRpm, IDLE, red * 0.86)
      const delta = target - state.rpm
      const step = clamp(delta, -900 * dt, 6200 * dt)
      state.rpm = clamp(state.rpm + step, IDLE, red * 1.03)
      state.shiftBlend = Math.max(0, (state.shiftBlend || 0) - dt * 2.4)
      if (Math.abs(target - state.rpm) < 45 || state.shiftBlend <= 0) {
        state.pendingShiftRpm = 0
        state.shiftBlend = 0
      }
    }

    state.shiftCool = Math.max(0, (state.shiftCool || 0) - dt)
    state.gPunch = Math.max(0, (state.gPunch || 0) - dt * 3.8)
    state.boostVentCool = Math.max(0, (state.boostVentCool || 0) - dt)

    // ---- Schaltlogik (ruhig, mit Cooldown gegen Hunting) ----
    // Hochschaltpunkt hängt von der Last ab: Vollgas spät (sportlich), wenig Gas
    // früh – damit bei Konstantfahrt in einen hohen Gang mit niedriger Drehzahl
    // geschaltet wird statt am Begrenzer zu kleben.
    const canShift = state.shiftCool <= 0
    const shiftUpPoint = red * (state.throttle > 0.7 ? 0.93 : state.throttle > 0.34 ? 0.82 : 0.42)
    const shiftDownPoint = state.throttle < 0.12 || braking ? 1700 : 1450

    if (canShift && state.rpm > shiftUpPoint && state.gear < maxGear) {
      const before = state.rpm
      state.gear++
      state.shift = 1
      state.shiftDir = 1
      state.shiftCool = 0.45
      state.rpm = Math.max(IDLE + 650, before * (0.7 + 0.03 * Math.min(state.gear, 5)))
      state.rpmDrop = Math.round(before - state.rpm)
      eventBoom('shiftUp')
      if (isTurbocharged(induction)) eventBoom('wastegate')
      if (induction.type === 'supercharger') eventBoom('whine')
    }
    if (canShift && state.gear > 1 && state.rpm < shiftDownPoint) {
      const before = state.rpm
      const audibleDownshift = state.throttle > 0.18 || (state.sensor && braking)
      state.gear--
      state.shiftDir = -1
      state.shiftCool = 0.45
      if (audibleDownshift) {
        state.shift = 1
        state.pendingShiftRpm = clamp(before * 1.18 + 320, IDLE, red * 0.8)
        state.shiftBlend = 0.34
        state.rpmDrop = -Math.round(state.pendingShiftRpm - before)
        eventBoom('shiftDown')
      } else {
        state.shift = 0
        state.rpm = clamp(state.rpm, IDLE, red * 0.8)
        state.rpmDrop = 0
        state.pendingShiftRpm = 0
        state.shiftBlend = 0
      }
    }

    // ---- Schubabschaltung / Overrun + Burble ----
    const offStrength = braking ? clamp((prevRpm - IDLE) / 3600 + 0.4, 0, 1) : 0
    state.overrun = smooth(state.overrun, offStrength, 1 - Math.pow(0.002, dt))
    if (braking && state.rpm > 1700 && Math.random() < dt * (0.6 + state.overrun * 1.6)) eventBoom('burble')
    const throttleDrop = (state._lastThro || 0) - state.throttle
    if (
      isTurbocharged(induction) &&
      state.boostVentCool <= 0 &&
      (state._lastThro || 0) > 0.52 &&
      throttleDrop > 0.035
    ) {
      state.boostVentCool = 0.38
      eventBoom('bov')
    }

    // ---- Automatischer Lastverlust bei statischem Gas ----
    if (
      state.settings.loadLoss &&
      Math.abs(state.throttle - (state._lastThro || 0)) < 0.012 &&
      state.throttle > 0.4
    ) {
      state.stable += dt
      if (state.stable > 2.0 && performance.now() - state.lastLoss > 3000) {
        state.lastLoss = performance.now()
        state.stable = 0
        state.overrun = Math.max(state.overrun, 0.22)
        eventBoom('burble')
      }
    } else {
      state.stable = 0
    }

    state._lastThro = state.throttle
    state._lastRpm = state.rpm
    state.engineBrake = braking ? clamp((prevRpm - state.rpm + 80) / 420, 0, 1) : 0
    state.shift = Math.max(0, state.shift - dt * 3.1)
  } catch (e) {
    console.error('[REVX] physics:', e.message)
  }
}
